import { Envelope, EnvelopeParticipant, EnvelopeSignedBy } from './envelope.model';

/**
 * Single source of truth for "what does this booking's waiver state mean?".
 *
 * Three surfaces used to answer that question independently — the Waivers
 * tab (`pivotByWaiver` + `mergeAttachedUnsent`), the waiver status badge
 * (`buildEnvelopeStatus`) and the reservation-info pill
 * (`aggregateEnvelopes`). They drifted apart more than once (see the
 * comments on those methods for the history), so the counting and the
 * requirement comparison now live here and each surface renders the
 * result rather than recomputing it.
 *
 * Pure by construction: no Firestore, no HTTP, no service injection. Every
 * lookup the evaluator needs (variant resolution, waiver names) is passed
 * in as a callback so callers own the I/O and this module stays trivially
 * testable.
 *
 * ---------------------------------------------------------------------
 * REQUIREMENT SCOPING — read this before changing `evaluateBookingCompliance`
 * ---------------------------------------------------------------------
 *
 * A booking's products attach waiver REFS (`productGroup.waivers[]` /
 * `product.waivers[]`). A ref is not a concrete waiver id — it is whatever
 * key the attach modal wrote, which under the group model is a
 * BuilderWaiverGroup id. Envelope items, by contrast, always carry a
 * CONCRETE resolved variant id (the adult or minor waiver the participant
 * was actually asked to sign).
 *
 * Comparing those two directly is the bug this module exists to fix: a
 * minor holding MINOR-B would not "match" the attached ref ADULT-A, and the
 * tab rendered a phantom "Unsent" adult row next to the correctly-signed
 * minor one. Every comparison here therefore happens in RESOLVED
 * variant-id space, via the caller-supplied `resolveVariant` (which wraps
 * the one and only variant resolver,
 * `WaiverBuilderService.resolveWaiverForParticipant`). There is deliberately
 * no second resolution implementation in this file.
 *
 * The second, subtler decision is WHICH requirements a given envelope is
 * on the hook for. Envelopes are not all created equal:
 *
 *   - Server dispatch at confirmation, backend Add Participant, and the
 *     customer-side Add Participant all create an envelope carrying the
 *     booking's FULL required-waiver set for that person.
 *   - In-person walk-in signing and kiosk QR signing create an envelope
 *     carrying exactly ONE waiver — the one being signed at that moment.
 *     Those flows are scoped to a signature, not to a booking roster:
 *     single-waiver entry context, "signer" wording throughout, anonymous
 *     name fallbacks ('Walk-in Signer' / 'Kiosk Signer'), no dedupe, and
 *     frequently no contact details at all (walk-ins deliberately omit
 *     recipientEmail/Phone, so there is no channel to send them anything
 *     further).
 *
 * Nothing in the schema distinguishes those two kinds of envelope —
 * `participant.role` is 'Guest' for both a genuine checkout guest and a
 * kiosk walk-in, and no other field declares the intent.
 *
 * So the evaluator scopes requirements BY ENVELOPE CONTENT: an envelope is
 * treated as representing ref R when it already holds an item for one of
 * the variants R can resolve to. A full-participant envelope naturally
 * covers every attached ref and is evaluated against all of them; a kiosk
 * envelope holding only waiver A is evaluated against A alone and does not
 * spontaneously acquire B and C.
 *
 * *** This is a backward-compatibility strategy, not a statement of what a
 * booking participant conceptually owes. *** It exists because the data
 * model cannot currently tell a full participant from a scoped signer. If
 * that distinction is ever modelled explicitly (an envelope `scope` field,
 * or a real participant entity), this scoping should be revisited — a
 * full participant should then be evaluated against the booking's whole
 * requirement set regardless of what its envelope happens to contain
 * today. Do not harden "an envelope only ever owes what it already holds"
 * into a business rule on the strength of this file.
 */

/** Per-item signing state, mirrored from `EnvelopeItemStatus`. */
export type ComplianceSignerStatus = 'pending' | 'signed';

/** Roll-up state for one waiver row, mirrored from `EnvelopeStatus`. */
export type ComplianceRowStatus = 'pending' | 'partial' | 'complete';

/**
 * An unresolved waiver reference attached to one of the booking's products
 * or product groups. `id` is the raw ref (group id or bare waiver id);
 * `name` is the best display label the caller could resolve for it.
 */
export interface ComplianceRequirementRef {
  id: string;
  name: string;
}

/**
 * One participant's line on a waiver row. Carries everything the Waivers
 * tab renders plus the envelope/item coordinates its actions need
 * (resend, revoke, in-person signing, signed-view).
 */
/**
 * Whether a signer line counts toward the participant's requirement as it
 * stands TODAY.
 *
 * - `current`   — counts. The ordinary case.
 * - `superseded` — the item is real and stays visible (a signature on it
 *   remains genuine evidence), but it no longer satisfies what this
 *   participant needs now, because they have aged past their threshold.
 *   Excluded from current-compliance counts; never deleted or rewritten.
 */
export type ComplianceRequirementState = 'current' | 'superseded';

export interface ComplianceSigner {
  participantName: string;
  /** Classification at ISSUANCE — what the stored envelope says. */
  isMinor: boolean;
  /**
   * Classification to apply to a signature being collected NOW — current
   * age where it can be determined, otherwise the issued classification
   * (see {@link classificationForNewRequirement}).
   *
   * Separate from {@link isMinor} on purpose. A participant who has aged
   * past their threshold is still recorded as a minor at issuance, but a
   * signature taken today must be collected as an adult: no guardian
   * fields, no under-age declaration. Sign-time surfaces read this;
   * the historical record reads `isMinor`.
   */
  currentIsMinor: boolean;
  status: ComplianceSignerStatus;
  /** Whether this line counts toward current compliance. */
  requirement: ComplianceRequirementState;
  /**
   * Set when this line is a requirement the participant has acquired by
   * ageing rather than an item that exists on their envelope. Nothing is
   * written for it — it is derived on every evaluation. Such a line has no
   * `itemId`, so it cannot be signed or resent through the item-based
   * actions; Commit 4 decides how staff collect it.
   */
  synthetic?: boolean;
  /** Why this line was superseded, when it was. */
  supersededBy?: AgeTransition;
  signedAt?: Date;
  signedBy?: EnvelopeSignedBy;
  envelopeId?: string;
  itemId?: string;
  recipientEmail?: string;
  recipientName?: string;
  recipientPhone?: string;
  bookingLabel?: string;
  companyId?: string;
  guardianName?: string;
  signedFormData?: { [fieldId: string]: any };
  waiverId?: string;
  waiverVersion?: number;
}

/**
 * One waiver on the booking, with every participant who has been asked to
 * sign it. `totalCount === 0` marks an "unsent" row: the waiver is attached
 * to a product but no envelope represents it yet.
 */
export interface ComplianceWaiverRow {
  waiverId: string;
  waiverName: string;
  waiverType: 'builder' | 'legacy';
  signers: ComplianceSigner[];
  /**
   * Counts over EVERY signer line on the row, superseded ones included.
   * This is the display view — "how many of the people asked have signed
   * this document" — and it stays truthful about history: a signed minor
   * waiver still reads as signed after the participant ages out.
   *
   * For what the booking currently owes, read the booking-level
   * `totalItems` / `signedItems`, or filter `signers` by
   * `requirement === 'current'`.
   */
  signedCount: number;
  totalCount: number;
  status: ComplianceRowStatus;
  /** Signer lines on this row that no longer count toward compliance. */
  supersededCount: number;
}

/**
 * A waiver a specific participant now needs because they have reached the
 * age threshold their requirement was established under. Purely derived —
 * nothing is persisted and no envelope item exists behind it.
 */
export interface AgeTransitionRequirement {
  participantName: string;
  /** Envelope the participant belongs to, for attribution in the UI. */
  envelopeId?: string;
  /** Concrete waiver they now need. */
  waiverId: string;
  waiverName: string;
  /**
   * True while no envelope item backs this requirement yet — staff can
   * materialize it onto the participant's existing envelope. False once a
   * real (still unsigned) item exists, at which point the ordinary
   * pending-item actions apply and the requirement is just waiting for a
   * signature.
   */
  synthetic: boolean;
}

/**
 * One participant's CURRENT compliance, regrouped out of a booking-level
 * evaluation (one envelope = one participant, so envelope id is the key).
 *
 * Exists so participant-by-participant surfaces don't re-derive which lines
 * count and which are signable — those are compliance semantics, and
 * duplicating them in a component is the drift this module exists to stop.
 */
export interface ParticipantCompliance {
  envelopeId: string;
  participantName: string;
  /** For setting up a signature collected NOW. The envelope's own `isMinor`
   *  is the historical record. */
  currentIsMinor: boolean;
  /** Lines counting toward what this participant owes today. */
  currentTotal: number;
  /** Those of them already signed. */
  currentSigned: number;
  /** Outstanding requirements backed by a real item — what a sign-time
   *  surface may offer. Excludes superseded items. */
  signableItemIds: string[];
  /** Acquired by ageing, with nothing written for them yet. Unsignable
   *  until materialized, but they still keep the participant incomplete. */
  unmaterializedWaiverIds: string[];
  /** False while an unmaterialized requirement is outstanding — this is what
   *  stops a superseded minor waiver reading as full compliance. */
  isComplete: boolean;
}

/**
 * Regroups a booking evaluation by participant. A participant with only
 * superseded lines still gets an entry (`currentTotal: 0`) so they never
 * vanish from a roster just because none of their items count any more.
 */
export function participantComplianceByEnvelope(
  compliance: BookingCompliance,
): Map<string, ParticipantCompliance> {
  const byEnvelope = new Map<string, ParticipantCompliance>();

  for (const row of compliance?.rows ?? []) {
    for (const signer of row?.signers ?? []) {
      const envelopeId = signer?.envelopeId;
      if (!envelopeId) continue;

      let entry = byEnvelope.get(envelopeId);
      if (!entry) {
        // Name and `currentIsMinor` are resolved once per envelope inside the
        // evaluator, so the first line establishes them for the group.
        entry = {
          envelopeId,
          participantName: signer.participantName,
          currentIsMinor: signer.currentIsMinor,
          currentTotal: 0,
          currentSigned: 0,
          signableItemIds: [],
          unmaterializedWaiverIds: [],
          isComplete: false,
        };
        byEnvelope.set(envelopeId, entry);
      }

      if (signer.requirement === 'superseded') continue;

      entry.currentTotal += 1;
      if (signer.status === 'signed') {
        entry.currentSigned += 1;
      } else if (signer.itemId) {
        entry.signableItemIds.push(signer.itemId);
      } else if (signer.waiverId) {
        entry.unmaterializedWaiverIds.push(signer.waiverId);
      }
    }
  }

  for (const entry of byEnvelope.values()) {
    entry.isComplete = entry.currentTotal > 0 && entry.currentSigned === entry.currentTotal;
  }

  return byEnvelope;
}

export interface BookingComplianceInput {
  /** Every envelope attached to the booking. */
  envelopes: Envelope[];
  /**
   * Unresolved refs attached to the booking's products/product groups.
   * Omit (badge / pill) to skip unsent-row detection entirely and get
   * counts over the issued items alone.
   */
  requiredRefs?: ComplianceRequirementRef[];
  /**
   * Wraps `WaiverBuilderService.resolveWaiverForParticipant`. Required
   * whenever `requiredRefs` is supplied — without it no ref can be mapped
   * into resolved-variant space and unsent detection is skipped.
   */
  resolveVariant?: (refId: string, isMinor: boolean) => string | undefined;
  /** Optional display-name lookup for a concrete waiver id. */
  waiverNameFor?: (waiverId: string) => string | undefined;
  /**
   * "Today" for age evaluation. Injected so birthday behavior is
   * deterministic in tests, and resolved ONCE per call so every envelope in
   * one evaluation shares the same effective date. Defaults to now.
   *
   * There are no timers anywhere: a booking modal left open across midnight
   * keeps the date it was evaluated with until something re-runs the
   * existing refresh/load path.
   */
  evaluationDate?: Date;
}

export interface BookingCompliance {
  /** Issued rows followed by unsent rows, sorted alphabetically overall. */
  rows: ComplianceWaiverRow[];
  /** Rows backed by at least one envelope item. */
  issuedRows: ComplianceWaiverRow[];
  /** Attached-but-unrepresented rows (`totalCount === 0`). */
  unsentRows: ComplianceWaiverRow[];
  /**
   * CURRENT-compliance counts — what the booking owes today.
   *
   * Superseded lines are excluded from both numerator and denominator, and
   * requirements acquired by ageing are included in the denominator. That
   * is what stops a signed-but-superseded minor waiver from reading as
   * "fully signed" once the participant has aged out.
   *
   * On a booking with no age transitions these are exactly the issued item
   * counts, so nothing about existing bookings changes.
   */
  totalItems: number;
  signedItems: number;
  pendingItems: number;
  /**
   * Lines that exist and remain visible but no longer count — historical
   * evidence. Not part of `totalItems`.
   */
  supersededItems: number;
  /** True when at least one participant has aged past their threshold. */
  hasAgeTransition: boolean;
  /**
   * One entry per waiver a participant now needs because they have aged
   * past their threshold and do not already hold it. Drives the
   * participant-level advisory copy — there may be several participants,
   * and a participant may need more than one waiver.
   *
   * Empty when nothing has transitioned, and empty when a transitioned
   * participant already holds the waiver they now need.
   */
  ageTransitionRequirements: AgeTransitionRequirement[];
  hasEnvelopes: boolean;
  /** Roll-up over the CURRENT-compliance counts above. */
  status: ComplianceRowStatus;
}

/**
 * Classification a participant was ISSUED under — the historical fact.
 *
 * Reads the stored flag and nothing else. Every creation path derives
 * `isMinor` at issuance (from date of birth where one was captured, from
 * the old checkbox before that) and writes it once; nothing rewrites it
 * afterwards, so this is stable for the life of the envelope.
 *
 * This is deliberately NOT "is this person a minor today" — see
 * {@link currentClassification} for that. Call sites should read
 * unambiguously: this one is asking what happened, that one is asking
 * what is true now.
 *
 * Note it is also NOT a reliable predictor of WHICH concrete waiver the
 * envelope holds. A participant classified a minor before the partner
 * configured a `minorWaiverId` legitimately received the adult variant.
 * Historical truth about the document comes from the stored item, never
 * from re-deriving it out of this flag.
 */
export function classificationAtIssue(participant?: EnvelopeParticipant): boolean {
  return !!participant?.isMinor;
}

/**
 * Classification a participant would receive TODAY — or null when that
 * cannot be determined.
 *
 * Requires BOTH a usable `dateOfBirth` and a usable
 * `minorAgeThresholdAtIssue` on the participant. Returning null rather
 * than a guess is the whole point: legacy participants and scoped signers
 * (kiosk QR, in-person walk-in) carry neither field, and they must keep
 * behaving exactly as they always have.
 *
 * The threshold comes from the PARTICIPANT SNAPSHOT, never from the
 * company's current setting. That is what makes a later change to
 * `companies/{id}.waiverMinorAgeThreshold` non-retroactive: a participant
 * established under 18 keeps being evaluated against 18 even after the
 * shop moves to 21, and two participants on one booking may legitimately
 * carry different thresholds. Nothing here reads company configuration.
 */
export function currentClassification(
  participant: EnvelopeParticipant | undefined,
  on: Date,
): boolean | null {
  const dateOfBirth = (participant?.dateOfBirth || '').trim();
  if (!dateOfBirth) return null;
  const threshold = participant?.minorAgeThresholdAtIssue;
  if (typeof threshold !== 'number' || !Number.isFinite(threshold) || threshold <= 0) return null;
  return deriveIsMinorFromDateOfBirth(dateOfBirth, threshold, on);
}

/**
 * How a participant's classification has moved since issuance.
 *
 * `minor-to-adult` is the only transition this system acts on — it is the
 * natural-aging case the product requires. Everything else is `none`:
 *
 *   - No date of birth or no threshold snapshot: not evaluable, so the
 *     historical classification simply stands.
 *   - Still a minor, or adult all along: nothing has changed.
 *   - Adult at issue but a minor today: only reachable from inconsistent
 *     data (a mistyped date of birth, say), since ageing cannot run
 *     backwards and the threshold is frozen at issuance. Treated as `none`
 *     deliberately — inventing a minor requirement from bad data would
 *     demand a guardian for someone who may well be an adult, which is a
 *     worse failure than leaving the record as issued.
 */
export type AgeTransition = 'none' | 'minor-to-adult';

export function detectAgeTransition(
  participant: EnvelopeParticipant | undefined,
  on: Date,
): AgeTransition {
  const now = currentClassification(participant, on);
  if (now === null) return 'none';
  const atIssue = classificationAtIssue(participant);
  return atIssue && !now ? 'minor-to-adult' : 'none';
}

/**
 * The classification to use when choosing a variant for a requirement
 * being added to this participant NOW — a product added mid-booking, for
 * instance.
 *
 * Prefers the current classification and falls back to the issuance one
 * when current age cannot be determined, so legacy participants and
 * scoped signers are unaffected while a DOB-bearing participant who has
 * aged out correctly receives the adult variant of anything newly
 * attached.
 *
 * Distinct from {@link satisfyingWaiverIdsForRef}, which spans BOTH
 * classifications to answer a historical representation question. This
 * yields exactly one classification, and therefore one variant.
 */
export function classificationForNewRequirement(
  participant: EnvelopeParticipant | undefined,
  on: Date = new Date(),
): boolean {
  const current = currentClassification(participant, on);
  return current === null ? classificationAtIssue(participant) : current;
}

/** Matches a calendar date in `YYYY-MM-DD` form. Shape check only. */
const DOB_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Splits a `YYYY-MM-DD` string into its calendar parts, or returns null
 * when the string is malformed or not a real date.
 *
 * Rejects impossible dates (`2025-02-30`, `2025-13-01`) by round-tripping
 * through a local `Date` and checking the components survived — JS silently
 * rolls overflow into the next month otherwise.
 *
 * Everything here is deliberately calendar arithmetic on local fields. A
 * birthday is a calendar fact, not an instant: parsing `'2008-09-18'` with
 * `new Date(string)` would treat it as UTC midnight and shift the day for
 * anyone west of Greenwich, flipping classification on the boundary date.
 */
function parseCalendarDate(value: string): { year: number; month: number; day: number } | null {
  if (typeof value !== 'string' || !DOB_PATTERN.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
    return null;
  }
  return { year, month, day };
}

/**
 * Whole years completed between `dateOfBirth` (a `YYYY-MM-DD` calendar
 * date) and `on`. Returns null when the input is not a real calendar date.
 *
 * Compares calendar fields only — `on`'s LOCAL year/month/day — so no
 * timezone conversion happens on either side.
 *
 * Boundary: a participant is their new age ON their birthday, not the day
 * after. Someone born 2008-09-18 evaluated on 2026-09-18 is 18.
 *
 * Leap-day birthdays: 2008-02-29 evaluated on 2026-02-28 is 17 (the day
 * has not been reached yet in a non-leap year); on 2026-03-01 it is 18.
 * This is the conventional reading and needs no special-casing — the
 * month/day comparison produces it naturally.
 */
export function ageOn(dateOfBirth: string, on: Date): number | null {
  const dob = parseCalendarDate(dateOfBirth);
  if (!dob || !(on instanceof Date) || isNaN(on.getTime())) return null;

  const onYear = on.getFullYear();
  const onMonth = on.getMonth() + 1;
  const onDay = on.getDate();

  let age = onYear - dob.year;
  const birthdayPassed = onMonth > dob.month || (onMonth === dob.month && onDay >= dob.day);
  if (!birthdayPassed) age -= 1;
  return age;
}

/**
 * Formats a `Date` as the `YYYY-MM-DD` calendar string the date-of-birth
 * fields use, reading LOCAL fields so no timezone shift can move the day.
 *
 * Used to bound the native date picker (`min` / `max`) with the same
 * representation the value itself is stored in.
 */
export function toCalendarDateString(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Oldest date a participant-capture field offers in its picker. Purely a
 * picker convenience — it stops the year spinner running back to year 1 —
 * and cannot exclude a living participant. {@link validateDateOfBirth}
 * still imposes no minimum or maximum age.
 */
export const DATE_OF_BIRTH_PICKER_MAX_AGE_YEARS = 120;

/** Why a date-of-birth entry was rejected, or null when it is usable. */
export type DateOfBirthProblem = 'missing' | 'malformed' | 'future';

/**
 * Validates a date-of-birth entry for the participant-capture forms.
 *
 * Deliberately does NOT impose a maximum age or a minimum age: no such
 * limit exists anywhere else in the product, and inventing one here would
 * reject legitimate participants (a 2-year-old on a family booking, a
 * 100-year-old) for no reason.
 */
export function validateDateOfBirth(value: string | undefined, today: Date = new Date()): DateOfBirthProblem | null {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'missing';
  const dob = parseCalendarDate(trimmed);
  if (!dob) return 'malformed';
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dobStart = new Date(dob.year, dob.month - 1, dob.day);
  if (dobStart.getTime() > todayStart.getTime()) return 'future';
  return null;
}

/**
 * Whether a date-of-birth entry blocks saving. Date of birth is optional,
 * so missing is allowed; malformed and future are not, since either would
 * derive a classification from a date nobody meant.
 *
 * Consequence at every call site: a participant saved WITHOUT one is
 * recorded as an adult. Since the "is under N" declaration was retired,
 * date of birth is the only remaining way to identify a minor — forms that
 * make it optional should say so.
 */
export function blocksSaveDateOfBirth(value: string | undefined, today: Date = new Date()): boolean {
  const problem = validateDateOfBirth(value, today);
  return problem !== null && problem !== 'missing';
}

/**
 * Derives the minor/adult classification for a participant being created
 * NOW, from their date of birth and the company's configured threshold.
 *
 * Returns null when the date of birth is unusable, so callers can tell
 * "not a minor" apart from "cannot say" and refuse to persist a guessed
 * classification.
 *
 * Note this yields ONE classification and therefore one waiver variant —
 * unlike {@link satisfyingWaiverIdsForRef}, which intentionally spans both
 * classifications. That one answers a historical question ("does this
 * envelope already represent this requirement?"); this one answers a
 * forward-looking question ("which waiver does this new person sign?").
 * Do not substitute one for the other.
 */
export function deriveIsMinorFromDateOfBirth(
  dateOfBirth: string | undefined,
  minorAgeThreshold: number,
  on: Date = new Date(),
): boolean | null {
  const trimmed = (dateOfBirth || '').trim();
  if (!trimmed) return null;
  const age = ageOn(trimmed, on);
  if (age === null) return null;
  if (!Number.isFinite(minorAgeThreshold) || minorAgeThreshold <= 0) return null;
  return age < minorAgeThreshold;
}

/**
 * The concrete waiver ids that can satisfy `refId`.
 *
 * Resolves the ref under BOTH classifications rather than only the
 * classification of the envelope being tested. A group normally yields two
 * ids (its active variant and its minor variant); a solo waiver or a group
 * with no minor variant collapses to one.
 *
 * Using the union matters for envelopes that hold the "other" variant for
 * their own classification — most commonly a minor whose envelope was
 * created BEFORE the partner configured a `minorWaiverId`, so it legitimately
 * carries the adult variant. Testing only against the minor resolution would
 * declare that ref unrepresented and re-introduce exactly the phantom row
 * this module removes. The union asks the weaker, safer question — "has this
 * participant been enrolled in this requirement at all?" — which is the
 * right question for a compatibility-preserving refactor.
 *
 * Falls back to the raw ref id when nothing resolves (unknown or dangling
 * ref), which matches the literal comparison the old code performed.
 */
export function satisfyingWaiverIdsForRef(
  refId: string,
  resolveVariant: (refId: string, isMinor: boolean) => string | undefined,
): Set<string> {
  const ids = new Set<string>();
  const adult = resolveVariant(refId, false);
  const minor = resolveVariant(refId, true);
  if (adult) ids.add(adult);
  if (minor) ids.add(minor);
  if (ids.size === 0) ids.add(refId);
  return ids;
}

/** Derives a row's roll-up state from its signed/total counts. */
function deriveRowStatus(signedCount: number, totalCount: number): ComplianceRowStatus {
  if (totalCount === 0 || signedCount === 0) return 'pending';
  if (signedCount >= totalCount) return 'complete';
  return 'partial';
}

/**
 * Evaluates a booking's waiver compliance from its envelopes and the
 * waiver refs its products currently attach.
 *
 * Two passes:
 *   1. Pivot every non-legacy envelope item into a per-waiver row, one
 *      signer line per (envelope, item). Each envelope is one participant,
 *      so this is inherently participant-aware — no booking-wide set of
 *      waiver ids is ever formed, which is what previously let one
 *      envelope's coverage stand in for another's.
 *   2. For each attached ref, ask whether ANY envelope represents it (see
 *      the scoping note at the top of this file). Refs nobody represents
 *      become `totalCount: 0` unsent rows.
 *
 * Legacy-typed items are dropped throughout, matching the long-standing
 * behavior of all three consuming surfaces — envelopes carry builder
 * waivers; legacy waivers live in the pre-envelope `rental.waiver[]` flow.
 */
export function evaluateBookingCompliance(input: BookingComplianceInput): BookingCompliance {
  const envelopes = Array.isArray(input?.envelopes) ? input.envelopes : [];
  const resolveVariant = input?.resolveVariant;
  const requiredRefs = Array.isArray(input?.requiredRefs) ? input.requiredRefs : [];
  const waiverNameFor = input?.waiverNameFor;
  // Resolved once so every envelope in this evaluation shares one "today".
  const evaluationDate = input?.evaluationDate ?? new Date();

  // --- Pass 1: issued items -> per-waiver rows ---------------------------
  const byWaiverId = new Map<string, ComplianceWaiverRow>();
  let totalItems = 0;
  let signedItems = 0;

  let supersededItems = 0;
  let hasAgeTransition = false;
  const ageTransitionRequirements: AgeTransitionRequirement[] = [];

  const rowFor = (waiverId: string, waiverName: string | undefined, waiverType: 'builder' | 'legacy') => {
    let row = byWaiverId.get(waiverId);
    if (!row) {
      row = {
        waiverId,
        // `createEnvelope` requires a name, but an item written before that
        // validation — or any malformed row — must not take the whole tab,
        // badge and pill down with it when the rows are sorted by name.
        waiverName: waiverName || waiverId,
        waiverType,
        signers: [],
        signedCount: 0,
        totalCount: 0,
        status: 'pending',
        supersededCount: 0,
      };
      byWaiverId.set(waiverId, row);
    }
    return row;
  };

  for (const envelope of envelopes) {
    if (!envelope) continue;
    const participant = envelope.participant;
    const isMinor = classificationAtIssue(participant);
    // Minors are routed to their guardian, so the guardian's name is the
    // meaningful "who do we chase" label on the row.
    const recipientName = isMinor && participant?.guardianName
      ? participant.guardianName
      : participant?.name;

    const items = (Array.isArray(envelope.items) ? envelope.items : [])
      .filter(item => item && item.waiverType !== 'legacy');

    // --- Age transition for THIS participant --------------------------
    // Evaluated per envelope: each carries its own threshold snapshot, so
    // two participants on one booking are judged independently and neither
    // consults the company's current setting.
    //
    // Requires a resolver to name the variant now needed. Without one
    // (a caller that only wants raw counts) the transition is not acted
    // on and behavior is exactly as it was before age evaluation existed.
    const transition = resolveVariant ? detectAgeTransition(participant, evaluationDate) : 'none';
    // Classification for any signature collected today. Same helper the
    // mid-booking product-add path uses, so sign-time and requirement-time
    // never disagree.
    const currentIsMinor = classificationForNewRequirement(participant, evaluationDate);

    // The concrete waivers this participant needs TODAY. Derived from the
    // items they actually hold — resolving each to the variant its group
    // yields for an ADULT — rather than from the booking's product refs.
    // Two reasons: it keeps Commit 1's envelope-content scoping intact (a
    // scoped signer does not acquire the whole booking's requirements),
    // and it asks the forward-looking question with ONE classification
    // rather than the historical both-variant question.
    const currentRequiredIds = new Set<string>();
    if (transition === 'minor-to-adult') {
      hasAgeTransition = true;
      for (const item of items) {
        currentRequiredIds.add(resolveVariant!(item.waiverId, false) ?? item.waiverId);
      }
    }
    const heldWaiverIds = new Set(items.map(i => i.waiverId));

    for (const item of items) {
      // After a transition, an item counts only if it IS one of the
      // variants now required. That covers the case where the envelope
      // already holds the adult variant — whether because no minor variant
      // existed at issuance or because one was appended later — in which
      // case nothing is superseded and no new requirement appears.
      const requirement: ComplianceRequirementState =
        transition === 'none' || currentRequiredIds.has(item.waiverId) ? 'current' : 'superseded';

      const row = rowFor(item.waiverId, item.waiverName, item.waiverType);
      row.signers.push({
        participantName: participant?.name ?? '',
        isMinor,
        currentIsMinor,
        status: item.status,
        requirement,
        ...(requirement === 'superseded' ? { supersededBy: transition } : {}),
        signedAt: item.signedAt,
        signedBy: item.signedBy,
        envelopeId: envelope.id,
        itemId: item.itemId,
        recipientEmail: envelope.recipientEmail,
        recipientName,
        recipientPhone: envelope.recipientPhone,
        guardianName: participant?.guardianName,
        bookingLabel: envelope.bookingLabel,
        companyId: envelope.companyId,
        signedFormData: item.signedFormData,
        waiverId: item.waiverId,
        waiverVersion: item.waiverVersion,
      });

      row.totalCount += 1;
      if (item.status === 'signed') row.signedCount += 1;

      if (requirement === 'superseded') {
        row.supersededCount += 1;
        supersededItems += 1;
      } else {
        totalItems += 1;
        if (item.status === 'signed') signedItems += 1;
      }
    }

    // Requirements this participant has acquired by ageing. An entry is
    // published for every one that is still OUTSTANDING, whether or not an
    // envelope item exists for it yet:
    //
    //   - no item        → a derived line is added to the row, and the entry
    //                      is flagged `synthetic` so staff can materialize it
    //   - item, pending  → the real item is already on the row; the entry is
    //                      still published because the waiver is still unsigned
    //   - item, signed   → satisfied, no entry
    //
    // Publishing the middle case is what keeps the advisory visible between
    // "staff added the waiver" and "the participant signed it". Basing it on
    // the absence of an item would clear the warning the moment the waiver
    // was added, which is precisely when it is most needed.
    for (const requiredId of currentRequiredIds) {
      const heldItem = items.find(i => i.waiverId === requiredId);

      if (heldItem) {
        if (heldItem.status !== 'signed') {
          ageTransitionRequirements.push({
            participantName: participant?.name ?? '',
            envelopeId: envelope.id,
            waiverId: requiredId,
            waiverName: heldItem.waiverName || requiredId,
            synthetic: false,
          });
        }
        continue;
      }

      // Derived only — nothing is written, no envelope item is appended, no
      // email is sent. The line carries no `itemId`, so the item-based
      // actions cannot act on it.
      const row = rowFor(requiredId, waiverNameFor?.(requiredId) || requiredId, 'builder');
      ageTransitionRequirements.push({
        participantName: participant?.name ?? '',
        envelopeId: envelope.id,
        waiverId: requiredId,
        waiverName: row.waiverName,
        synthetic: true,
      });
      row.signers.push({
        participantName: participant?.name ?? '',
        isMinor,
        currentIsMinor,
        status: 'pending',
        requirement: 'current',
        synthetic: true,
        envelopeId: envelope.id,
        recipientEmail: envelope.recipientEmail,
        recipientName,
        recipientPhone: envelope.recipientPhone,
        guardianName: participant?.guardianName,
        bookingLabel: envelope.bookingLabel,
        companyId: envelope.companyId,
        waiverId: requiredId,
      });
      row.totalCount += 1;
      totalItems += 1;
    }
  }

  const issuedRows = Array.from(byWaiverId.values());
  for (const row of issuedRows) {
    row.status = deriveRowStatus(row.signedCount, row.totalCount);
  }

  // --- Pass 2: attached refs nobody represents -> unsent rows ------------
  const unsentRows: ComplianceWaiverRow[] = [];
  if (requiredRefs.length > 0 && resolveVariant) {
    const seenUnsentIds = new Set<string>();

    for (const ref of requiredRefs) {
      if (!ref?.id) continue;
      const satisfying = satisfyingWaiverIdsForRef(ref.id, resolveVariant);

      // Already issued to somebody? Then the requirement is on the board
      // and its row (pending or signed) is rendered by pass 1.
      let represented = false;
      for (const id of satisfying) {
        if (byWaiverId.has(id)) { represented = true; break; }
      }
      if (represented) continue;

      // Display the row as the variant that would actually be sent, rather
      // than the raw ref — a ref is frequently a group id, which is not a
      // waiver anybody can open or sign.
      const displayId = resolveVariant(ref.id, false) ?? ref.id;
      if (seenUnsentIds.has(displayId)) continue;
      seenUnsentIds.add(displayId);

      unsentRows.push({
        waiverId: displayId,
        waiverName: waiverNameFor?.(displayId) || ref.name,
        waiverType: 'builder',
        signers: [],
        signedCount: 0,
        totalCount: 0,
        status: 'pending',
        supersededCount: 0,
      });
    }
  }

  const rows = [...issuedRows, ...unsentRows]
    .sort((a, b) => (a.waiverName || '').localeCompare(b.waiverName || ''));

  return {
    rows,
    issuedRows,
    unsentRows,
    totalItems,
    signedItems,
    pendingItems: totalItems - signedItems,
    supersededItems,
    hasAgeTransition,
    ageTransitionRequirements,
    hasEnvelopes: envelopes.length > 0,
    status: deriveRowStatus(signedItems, totalItems),
  };
}
